import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';
import ButtonDesigned from '../../../../components/UI-Elements/Buttons/ButtonDesigned';
import { LinearGauge } from '../../../../components/UI-Elements/Charts/LinearGauge/LinearGauge';
import { OverlayDisplayDiv } from '../../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { IBalanceFlowProps } from '../../../../sp-types/balanceFlowType';
import { IMaterialExposure } from '../../../../sp-types/netExposureType';
import { createEmptyRiskTolerance, getRiskTolerance } from '../../../../utils/accountUtils';
import { getExposureColor, getExposureStatus } from '../../../../utils/RiskUtils';
import { getFinancialPositionsSum } from '../../balanceUtils';
import BalanceChartCard from '../BalanceChartCard';
import { CloseButton } from '@components/UI-Elements/Buttons/CloseButton';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

export interface IBalanceTotals extends IMaterialExposure {
  balanceDate: string;
  netExposure: number;
}

export const BalanceFlowCloser: React.FC<IBalanceFlowProps> = ({
  balanceFlowData,
  setCurrentStepIndex,
  updateBalanceFlowData,
}) => {
  const {
    exposureBeforeBalance,
    formulaId,
    materialExposure: totalAmounts,
    balanceDate,
    balanceInstructions,
  } = balanceFlowData;

  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();

  const [financialPositionsValue] = useState<number>(
    getFinancialPositionsSum(balanceInstructions || [], pricesMap[formulaId] || 0),
  );
  const { account } = useAccountStore();
  const navigate = useNavigate();
  const [isOverlay, setIsOverlay] = useState(false);
  const [isButtonFunctionLoading, setIsButtonFunctionLoading] = useState(false);

  const netExposurePrev =
    totalAmounts.inventorySum + Math.abs(totalAmounts.financialPositions) - totalAmounts.futureOrder;
  const netExposure =
    totalAmounts.inventorySum + totalAmounts.financialPositions + financialPositionsValue - totalAmounts.futureOrder;
  // maybe it will be better to take the exposure from "useExposureStore"...

  async function closeBalance() {
    setIsButtonFunctionLoading(true);
    await updateBalanceFlowData({
      ...balanceFlowData,
      status: 'completed',
    });
    navigate('/dashboard');
    setIsButtonFunctionLoading(false);
  }

  return (
    <>
      {isOverlay && (
        <OverlayDisplayDiv setDisplayOverlay={setIsOverlay} hideClose>
          <div className="bg-white shadow-sm flex flex-col justify-center items-center gap-4 py-4 px-2">
            <div className="flex flex-1 w-full justify-end items-end -m-4">
              <CloseButton onClick={closeBalance} isBlack />
            </div>
            <img src="/assets/icons/colored/on-target.svg" alt="target" className="w-10 h-10" />
            <p className="text-lg2">Balancing completed!</p>
            <p className="text-sm text-gray-700 mx-2">
              Your {materialsMap[formulaId].name} has been successfully balanced.
            </p>
            <ButtonDesigned style="primary" className="py-2 px-4" onButtonClick={closeBalance}>
              Close
            </ButtonDesigned>
          </div>
        </OverlayDisplayDiv>
      )}
      <div className="flex flex-1 flex-col gap-4">
        <h2 className="text-lg">Balance summary</h2>
        <h6 className="text-red mb-[1rem]   ">Main cause</h6>

        <div className="flex flex-col mx-auto w-[70%]">
          <LinearGauge
            hightInRem={1.125}
            currentValue={exposureBeforeBalance + financialPositionsValue}
            riskTolerance={getRiskTolerance(formulaId, account) || createEmptyRiskTolerance()}
            numberOfTicks={12}
          />
        </div>

        <div className="flex flex-row justify-evenly mx-auto w-full gap-8">
          <BalanceChartCard
            materialExposureData={totalAmounts}
            title="Previous balance"
            balanceDate={balanceDate.toDate().toLocaleDateString()}
            netExposure={netExposurePrev}
            exposureColor={getExposureColor(getExposureStatus(getRiskTolerance(formulaId, account)!, netExposurePrev))}
          />
          <BalanceChartCard
            materialExposureData={{
              ...totalAmounts,
              financialPositions: totalAmounts.financialPositions + financialPositionsValue,
            }}
            netExposure={netExposure}
            title="Current balance"
            balanceDate={balanceDate.toDate().toLocaleDateString()}
            exposureColor={getExposureColor(getExposureStatus(getRiskTolerance(formulaId, account)!, netExposure))}
          />
        </div>

        <div className="flex flex-row mt-auto gap-3">
          <div>
            <ButtonDesigned
              backButton
              className="py-2"
              style="outline"
              onButtonClick={() => setCurrentStepIndex((currIndex) => currIndex - 1)}
            ></ButtonDesigned>
          </div>
          <ButtonDesigned onButtonClick={() => setIsOverlay(true)}>Complete</ButtonDesigned>
        </div>
      </div>
    </>
  );
};
